import React, { useState } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { useProjectManagement } from '../hooks/useProjectManagement';
import { ProjectStats } from '../components/project/ProjectStats';
import { ProjectFilters } from '../components/project/ProjectFilters';
import { ProjectListTable } from '../components/project/ProjectListTable';
import { EditProjectModal } from '../components/project/EditProjectModal';
import { ImportProjectModal } from '../components/project/ImportProjectModal';
import { Folder, Plus, Download, CheckCircle, RefreshCw } from 'lucide-react';

const ProjectManagement: React.FC = () => {
    const hooks = useProjectManagement();
    const { navigate, importProjectData } = useAppContext();

    // Local Modal State
    const [showImportModal, setShowImportModal] = useState(false);
    const [editingProjectName, setEditingProjectName] = useState<string | null>(null);

    const handleImportJSON = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            const data = JSON.parse(text);
            await importProjectData(data, false);
            alert(`Project imported successfully!`);
            hooks.refreshBackendProjects();
            setShowImportModal(false);
        } catch (error: any) {
            console.error("Import failed", error);
            alert("Failed to import project: " + error);
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                            <Folder className="text-primary" /> Project Management
                        </h1>
                        <p className="text-text-secondary mt-1">Comprehensive project and customer management workspace.</p>
                    </div>
                    <button
                        onClick={() => navigate('/project-creator')}
                        className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm shadow-lg hover:shadow-xl transition-all"
                    >
                        <Plus size={18} /> New Project
                    </button>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={hooks.handleSaveActiveProject}
                        disabled={!hooks.isActiveProject || hooks.isSaving}
                        className={`flex items-center gap-2 px-4 py-2 border rounded text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${hooks.saveSuccess
                            ? 'bg-emerald-500 text-white border-emerald-600 shadow-lg shadow-emerald-500/50 scale-105'
                            : 'bg-green-600 text-white border-green-700 hover:bg-green-700'
                            }`}
                        title="Save active project to backend"
                    >
                        {hooks.saveSuccess ? (
                            <><CheckCircle size={16} /> Saved!</>
                        ) : hooks.isSaving ? (
                            <><RefreshCw size={16} className="animate-spin" /> Saving...</>
                        ) : (
                            <><Download size={16} /> Save Active Project</>
                        )}
                    </button>
                    <button
                        onClick={() => hooks.refreshBackendProjects()}
                        className="flex items-center gap-2 px-4 py-2 bg-background border border-border rounded hover:bg-muted text-sm"
                    >
                        <RefreshCw size={16} /> Refresh
                    </button>
                    <button
                        onClick={() => setShowImportModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-background border border-border rounded hover:bg-muted text-sm"
                    >
                        <Download size={16} /> Import
                    </button>
                </div>
            </div>

            {/* Dashboard Cards */}
            <ProjectStats
                projects={hooks.projectTableData}
                fmeaItemsCount={hooks.allData.fmeaItems?.length || 0}
            />

            {/* Filters */}
            <ProjectFilters
                searchQuery={hooks.searchQuery}
                setSearchQuery={hooks.setSearchQuery}
                statusFilter={hooks.statusFilter}
                setStatusFilter={hooks.setStatusFilter}
                stageFilter={hooks.stageFilter}
                setStageFilter={hooks.setStageFilter}
            />

            {/* Project Table */}
            <ProjectListTable
                projects={hooks.filteredAndSortedProjects}
                loadingProject={hooks.loadingProject}
                loadSuccess={hooks.loadSuccess}
                onLoad={hooks.handleLoadProject}
                onExport={hooks.handleExport}
                onDelete={hooks.handleDelete}
                onEdit={(name) => setEditingProjectName(name)}
                onSort={hooks.toggleSort}
                canDelete={hooks.canDelete}
                totalBackendProjects={hooks.projects.length}
            />

            {/* Modals */}
            <ImportProjectModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImport={handleImportJSON}
            />

            <EditProjectModal
                isOpen={!!editingProjectName}
                projectName={editingProjectName}
                onClose={() => setEditingProjectName(null)}
                onSaveSuccess={() => {
                    hooks.refreshBackendProjects();
                    // Ideally we should also reload if active project loaded, but refreshBackendProjects handles the list sync.
                    // The component's effects trigger list refresh.
                }}
            />
        </div>
    );
};

export default ProjectManagement;
